import React from 'react';

/**
 * Production-grade Error Boundary
 * Catches React render errors and shows a friendly UI instead of a white screen.
 * Logs errors to console (and could be wired to Sentry/LogRocket later).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[HCEN ErrorBoundary]', this.props.scope || 'app', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const scoped = !!this.props.scope;
    const title = scoped ? `This page hit an error` : 'Something went wrong';
    const subtitle = scoped
      ? `The rest of the dashboard is still running. You can retry this page or go back to Dashboard.`
      : `We've been notified and are working on it. Your data is safe. Try reloading the page or go back to the dashboard.`;

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--auth-page-bg)',
        padding: 20,
        fontFamily: 'Outfit, sans-serif',
      }}>
        <div style={{
          maxWidth: 520,
          width: '100%',
          background: 'var(--surface-subtle)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 16,
          padding: '40px 32px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>⚠️</div>
          <h1 style={{
            fontSize: 24,
            fontWeight: 800,
            color: '#fff',
            marginBottom: 12,
          }}>
            {title}
          </h1>
          <p style={{
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 28,
            lineHeight: 1.6,
          }}>
            {subtitle}
          </p>

          {this.state.error && (
            <details style={{
              textAlign: 'left',
              background: 'rgba(0,0,0,0.3)',
              padding: '12px 14px',
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 12,
              color: '#fbbf24',
              fontFamily: 'monospace',
              maxHeight: 200,
              overflow: 'auto',
            }}>
              <summary style={{ cursor: 'pointer', marginBottom: 8, color: '#f87171' }}>
                Technical details
              </summary>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </div>
            </details>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {scoped && (
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '10px 22px',
                  background: 'linear-gradient(135deg, #4f8ef7, #6366f1)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Try Again
              </button>
            )}
            <button
              onClick={this.handleReload}
              style={{
                padding: '10px 22px',
                background: 'linear-gradient(135deg, #00d4aa, #00b894)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              🔄 Reload Page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: '10px 22px',
                background: 'var(--border-color)',
                color: '#fff',
                border: '1px solid var(--surface-strong)',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              🏠 Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
